import type { ReactNode } from 'react';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import {
  asDataIndexListType,
  asDataIndexType,
  DEFAULT_DATA_INDEX_TYPE,
  dataIndexListPath,
  legacyDataIndexListPath,
  type DataIndexListType,
} from './urls';

type DataIndexTypeResolverProps = {
  segment: string;
  /**
   * What to render for a segment this page has no list for. The `journeys`
   * twin passes the embedded host, because navigating from there parks the
   * transition: ownership leaves such a segment to the embedded app, so the
   * active bundle is Journeys and `safeNavigate` waits for a host that this
   * route never mounts. Omitted, the reader is sent on instead.
   */
  fallback?: ReactNode;
  children: (type: DataIndexListType) => ReactNode;
};

/**
 * Resolves the `:type` segment to one of the two lists this page serves.
 *
 * The object segments resolve to no list here. They name rows that this page
 * reaches by choosing an object type on the attributes list, which writes the
 * query rather than the path — but they are the embedded app's own list URLs,
 * which is why neither spelling is claimed. Asked for one on the migrated
 * spelling, the reader is handed to the URL that app answers rather than
 * dropped on the attributes list, which would lose the page they asked for.
 *
 * A segment naming no browser at all is not an error page: the embedded app
 * sends anyone who types one to the attributes list, in two hops — the type
 * route bounces it to `data-index` (`app/pods/data-index/type/route.js:8-12`),
 * which redirects to attributes (`app/pods/data-index/index/route.ts:21`) —
 * and so does this, in one.
 *
 * This sits outside the flag check, so the normalising happens here even
 * while the embedded app is serving the page. The URL it lands on is one
 * both apps answer, so the reader cannot tell; it is only worth knowing when
 * reasoning about what "ships dark" covers.
 *
 * On the `journeys` twin neither hand-off is the right answer — see
 * `fallback`.
 */
export function DataIndexTypeResolver({ segment, fallback, children }: DataIndexTypeResolverProps) {
  const listType = asDataIndexListType(segment);
  const embeddedType = asDataIndexType(segment);
  const href = useMigrationHandoffHref((workspaceId) =>
    embeddedType == null
      ? dataIndexListPath(workspaceId, DEFAULT_DATA_INDEX_TYPE)
      : legacyDataIndexListPath(workspaceId, embeddedType),
  );

  if (listType == null) {
    if (fallback !== undefined) {
      return <>{fallback}</>;
    }
    return href == null ? null : <SafeNavigate to={href} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return <>{children(listType)}</>;
}
